// Módulo Arquivos — cópia fiel (01/10/2026, Walber).
// Monta NO NAVEGADOR uma cópia autônoma (HTML) de um relatório, igual a como ele sai na impressão,
// e envia ao Arquivos. Nada é gerado no servidor (Handoff 2.1: nada de Chromium/PDF no servidor).
// Como funciona:
//  1) copia a "área impressa" do relatório (ex.: #fad07-papel), com o que está digitado nos campos;
//  2) tira dela o que nunca sai no papel (.noprint etc.), scripts e eventos;
//  3) junta todo o estilo da página; as regras de impressão (@media print) valem também na tela,
//     para a prévia do Arquivos mostrar exatamente o papel;
//  4) embute imagens e fontes do próprio TOVIS (a cópia não depende de arquivos que mudam a cada publicação);
//  5) a regra global do FAD-07 PE que esconde tudo na impressão é neutralizada SÓ dentro da cópia.
import { Arquivo, NovoArquivo, arquivosApi } from './arquivosApi';

export type PedidoArquivo = Omit<NovoArquivo, 'arquivo' | 'nomeArquivo'>;

/** Ajustes só da cópia: título no alto da folha e/ou folha deitada (A4 paisagem). */
export interface OpcoesCopia { cabecalho?: string; paisagem?: boolean }

export const AVISO_D1 = 'IIP e IB saem sem os positivos do laboratório (pendência D1). Esses números ficam congelados nesta cópia.';

const CLASSES_FORA_DO_PAPEL = ['noprint', 'no-print', 'qp-noprint', 'aud-noprint', 'fc-noprint', 'lq-noprint'];
const TAGS_PROIBIDAS = 'script, iframe, object, embed, noscript, template';
const LIMITE_EMBUTIR = 1.5 * 1024 * 1024;

// ── Avisos vindos da conferência do laboratório (FAD-07) ────────────────────
interface BoletimConf { sem_resultado?: number | null; congeladas?: number | null }
interface GrupoConf { recusa_sispncd?: boolean | null; boletins?: BoletimConf[] | null }

export function avisosDaConferencia(grupos: readonly GrupoConf[] | null | undefined): string[] {
  if (!grupos || !grupos.length) return [];
  const avisos: string[] = [];
  if (grupos.some(g => g.recusa_sispncd)) avisos.push('Será recusado no SISPNCD: imóveis positivos maiores que depósitos.');
  const boletins = grupos.flatMap(g => g.boletins ?? []);
  const sem = boletins.reduce((s, b) => s + (b.sem_resultado ?? 0), 0);
  const cong = boletins.reduce((s, b) => s + (b.congeladas ?? 0), 0);
  if (sem) avisos.push(`${sem} ${sem === 1 ? 'amostra estava' : 'amostras estavam'} sem resultado do laboratório.`);
  if (cong) avisos.push(`${cong} ${cong === 1 ? 'amostra estava congelada' : 'amostras estavam congeladas'}.`);
  return avisos;
}

// ── Estilos ─────────────────────────────────────────────────────────────────
function ehImpressao(r: CSSMediaRule): boolean {
  return Array.from(r.media).some(m => m.trim().toLowerCase() === 'print');
}

function textoDaRegra(r: CSSRule): string {
  if (typeof CSSMediaRule !== 'undefined' && r instanceof CSSMediaRule && ehImpressao(r)) {
    const dentro = Array.from(r.cssRules)
      .filter(x => !(typeof CSSPageRule !== 'undefined' && x instanceof CSSPageRule))
      .map(x => x.cssText)
      .join('\n');
    return `${r.cssText}\n${dentro}`;
  }
  return r.cssText;
}

/** Troca url(relativo) por url(absoluto), usando o endereço da folha de estilo como base. */
function absolutizarUrls(css: string, base: string): string {
  return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (inteiro, _aspas: string, bruto: string) => {
    const u = bruto.trim();
    if (u.startsWith('data:') || u.startsWith('#') || u.startsWith('blob:')) return inteiro;
    try { return `url("${new URL(u, base).href}")`; } catch { return inteiro; }
  });
}

async function paraDataUrl(url: string): Promise<string | null> {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const b = await r.blob();
    if (b.size > LIMITE_EMBUTIR) return null;
    return await new Promise<string>((ok, falha) => {
      const leitor = new FileReader();
      leitor.onload = () => ok(String(leitor.result));
      leitor.onerror = () => falha(leitor.error);
      leitor.readAsDataURL(b);
    });
  } catch {
    return null;
  }
}

async function embutirArquivosDoTovis(css: string): Promise<string> {
  const origem = window.location.origin;
  const achados = new Set<string>();
  const re = /url\("([^"]+)"\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(css))) {
    if (m[1].startsWith(origem + '/')) achados.add(m[1]);
  }
  let saida = css;
  for (const url of Array.from(achados)) {
    const dados = await paraDataUrl(url);
    if (dados) saida = saida.split(`url("${url}")`).join(`url("${dados}")`);
  }
  return saida;
}

/** Lê TODO o estilo da página de uma vez, sem esperar nada (a foto é tirada no mesmo instante do conteúdo). */
function lerEstilos(): { css: string; links: string[] } {
  const partes: string[] = [];
  const links: string[] = [];
  for (const folha of Array.from(document.styleSheets)) {
    let regras: CSSRuleList | null = null;
    try { regras = folha.cssRules; } catch { regras = null; }
    if (regras) {
      const texto = Array.from(regras).map(textoDaRegra).join('\n');
      partes.push(absolutizarUrls(texto, folha.href || document.baseURI));
    } else if (folha.href) {
      links.push(folha.href); // folha de outro site (ex.: Google Fonts): fica como link
    }
  }
  return { css: partes.join('\n'), links };
}

// ── Conteúdo ────────────────────────────────────────────────────────────────
/** Leva para a cópia o que está digitado/escolhido nos campos (o clone não leva sozinho). */
function copiarValores(original: Element, copia: Element) {
  const o = original.querySelectorAll('input, textarea, select');
  const c = copia.querySelectorAll('input, textarea, select');
  o.forEach((el, i) => {
    const alvo = c[i];
    if (!alvo) return;
    if (el instanceof HTMLInputElement && alvo instanceof HTMLInputElement) {
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) alvo.setAttribute('checked', ''); else alvo.removeAttribute('checked');
      } else {
        alvo.setAttribute('value', el.value);
      }
    } else if (el instanceof HTMLTextAreaElement && alvo instanceof HTMLTextAreaElement) {
      alvo.textContent = el.value;
    } else if (el instanceof HTMLSelectElement && alvo instanceof HTMLSelectElement) {
      Array.from(alvo.options).forEach((op, j) => {
        if (j === el.selectedIndex) op.setAttribute('selected', ''); else op.removeAttribute('selected');
      });
    }
  });
  const oc = original.querySelectorAll('canvas');
  const cc = copia.querySelectorAll('canvas');
  oc.forEach((cv, i) => {
    const alvo = cc[i];
    if (!alvo) return;
    try {
      const img = document.createElement('img');
      img.src = cv.toDataURL('image/png');
      img.setAttribute('style', alvo.getAttribute('style') || '');
      img.setAttribute('width', String(cv.width));
      img.setAttribute('height', String(cv.height));
      alvo.replaceWith(img);
    } catch { /* gráfico protegido: fica como está */ }
  });
}

/** Tira da cópia o que nunca sai no papel, e qualquer código executável. */
function limpar(raiz: Element) {
  raiz.querySelectorAll(TAGS_PROIBIDAS).forEach(n => n.remove());
  raiz.querySelectorAll(CLASSES_FORA_DO_PAPEL.map(c => `.${c}`).join(',')).forEach(n => n.remove());
  const todos = [raiz, ...Array.from(raiz.querySelectorAll('*'))];
  for (const el of todos) {
    for (const at of Array.from(el.attributes)) {
      const nome = at.name.toLowerCase();
      const valor = at.value.trim().toLowerCase();
      if (nome.startsWith('on')) el.removeAttribute(at.name);
      else if (['href', 'src', 'action', 'formaction', 'xlink:href'].includes(nome) && valor.startsWith('javascript:')) el.removeAttribute(at.name);
    }
  }
}

async function embutirImagens(raiz: Element) {
  for (const img of Array.from(raiz.querySelectorAll('img'))) {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) continue;
    let absoluto: string;
    try { absoluto = new URL(src, document.baseURI).href; } catch { continue; }
    const dados = await paraDataUrl(absoluto);
    img.setAttribute('src', dados ?? absoluto);
    img.removeAttribute('srcset');
  }
}

function escapar(t: string): string {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Copia uma ou várias partes da tela (na ordem dada) para dentro de uma caixa solta. */
function copiarPartes(elementos: Element[]): Element {
  if (elementos.length === 1) {
    const copia = elementos[0].cloneNode(true) as Element;
    copiarValores(elementos[0], copia);
    return copia;
  }
  const caixa = document.createElement('div');
  caixa.className = 'tovis-copia-partes';
  for (const el of elementos) {
    const copia = el.cloneNode(true) as Element;
    copiarValores(el, copia);
    caixa.appendChild(copia);
  }
  return caixa;
}

/** Monta a página autônoma (cópia fiel) a partir da área impressa de um relatório.
 *  Tudo o que vem da tela (conteúdo e estilo) é lido NA HORA da chamada, antes de qualquer espera:
 *  dá para chamar logo antes de uma ação que muda a tela (ex.: fechar a área) sem perder a foto. */
export async function montarCopiaFiel(elemento: Element | Element[], titulo: string, opcoes: OpcoesCopia = {}): Promise<string> {
  const lista = Array.isArray(elemento) ? elemento : [elemento];
  if (!lista.length) throw new Error('O relatório não está na tela.');
  const copia = copiarPartes(lista);
  limpar(copia);
  const lidos = lerEstilos();
  await embutirImagens(copia);
  const css = await embutirArquivosDoTovis(lidos.css);
  const links = lidos.links;
  const cssSeguro = css.replace(/<\/style/gi, '<\\/style');
  return [
    '<!doctype html>',
    '<html lang="pt-BR">',
    '<head>',
    '<meta charset="utf-8">',
    `<title>${escapar(titulo)}</title>`,
    `<base href="${escapar(window.location.origin + '/')}">`,
    `<meta name="tovis-copia-fiel" content="${escapar(new Date().toISOString())}">`,
    ...links.map(h => `<link rel="stylesheet" href="${escapar(h)}">`),
    `<style>${cssSeguro}</style>`,
    '<style>',
    '/* Arquivos: a cópia mostra na tela o mesmo que sai na impressão */',
    'html, body { background: #fff !important; }',
    'body { margin: 0; padding: 16px; }',
    'body * { visibility: visible !important; }',
    '/* tabelas com barra de rolagem saem inteiras na cópia */',
    '.MuiTableContainer-root { overflow: visible !important; max-height: none !important; }',
    '.tovis-cabecalho-copia { font: 700 16px Arial, Helvetica, sans-serif; color: #17211D; margin: 0 0 12px; }',
    '@media print { body { padding: 0 !important; } }',
    ...(opcoes.paisagem ? ['@page { size: A4 landscape; margin: 10mm; }'] : []),
    '</style>',
    '</head>',
    '<body>',
    ...(opcoes.cabecalho ? [`<div class="tovis-cabecalho-copia">${escapar(opcoes.cabecalho)}</div>`] : []),
    copia.outerHTML,
    '</body>',
    '</html>',
  ].join('\n');
}

/** Guarda uma página HTML já pronta (ex.: Termo/Resumo/Conciliação do Fechamento de Área). */
export async function arquivarHtmlPronto(token: string | null, html: string, pedido: PedidoArquivo) {
  const blob = new Blob([html], { type: 'text/html' });
  return arquivosApi.arquivar(token, { ...pedido, arquivo: blob, nomeArquivo: 'copia.html' });
}

/** Monta a cópia fiel de uma área impressa e guarda no Arquivos. */
export async function arquivarElemento(token: string | null, elemento: Element | Element[], pedido: PedidoArquivo, opcoes: OpcoesCopia = {}) {
  const html = await montarCopiaFiel(elemento, pedido.titulo, opcoes);
  return arquivarHtmlPronto(token, html, pedido);
}

/** Garante que uma página HTML pronta (ex.: Termo do Fechamento de Área) comece como página completa. */
export function comoPaginaCompleta(html: string): string {
  const t = html.trim();
  return /^<!doctype html|^<html[\s>]/i.test(t) ? t : `<!doctype html>\n${t}`;
}

/** Versão atual já guardada deste mesmo documento (mesma chave), se houver. */
export async function versaoAtual(token: string | null, pedido: PedidoArquivo): Promise<Arquivo | null> {
  const lista = await arquivosApi.listar(token, {
    pasta: pedido.pasta,
    ciclo_id: pedido.ciclo_id ?? undefined,
    area_id: pedido.area_id ?? undefined,
    se: pedido.se ?? undefined,
    limite: 1000,
  });
  return lista.find(a => a.chave === pedido.chave) ?? null;
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/** 'AAAA-MM' → 'Setembro/2026' */
export function mesPorExtenso(aaaamm: string): string {
  const [ano, mes] = aaaamm.split('-');
  const n = Number(mes);
  return n >= 1 && n <= 12 ? `${MESES[n - 1]}/${ano}` : aaaamm;
}

/** Abre o módulo Arquivos já na pasta indicada. */
export function abrirNoArquivos(pasta: string) {
  try { sessionStorage.setItem('tovis-arquivos-filtros', JSON.stringify({ vista: pasta, ciclo: '', area: '' })); } catch { /* sem armazenamento */ }
  window.location.assign('/arquivos');
}
